import { Outlet, Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { useTenantStore } from '@/store/tenantStore'
import { LogOut, User, BookOpen, LayoutDashboard, Brain, Headphones, StickyNote, ClipboardCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { NotificationBadge } from '@/features/messages'
import { ModeSwitcher } from './ModeSwitcher'
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher'

export default function MainLayout() {
  const { user, logout } = useAuthStore()
  const { tenantInfo } = useTenantStore()
  const navigate = useNavigate()
  const { t } = useTranslation('nav')

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/my/dashboard" className="flex items-center space-x-3">
              <img
                src={tenantInfo?.logoUrl || '/lgcns-logo.png'}
                alt={tenantInfo?.name || 'Quebon'}
                className="w-auto h-8"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/lgcns-logo.png'
                }}
              />
              <span className="font-semibold text-gray-900">
                {tenantInfo?.displayName || tenantInfo?.name || 'Quebon B2B'}
              </span>
            </Link>

            {/* Navigation */}
            <nav className="flex items-center space-x-6">
              <Link
                to="/my/dashboard"
                className="flex items-center space-x-1 text-gray-600 hover:text-gray-900"
              >
                <LayoutDashboard className="w-5 h-5" />
                <span>{t('learner.dashboard')}</span>
              </Link>
              <Link
                to="/my/courses"
                className="flex items-center space-x-1 text-gray-600 hover:text-gray-900"
              >
                <BookOpen className="w-5 h-5" />
                <span>{t('learner.courses')}</span>
              </Link>
              <Link
                to="/my/aiq"
                className="flex items-center space-x-1 text-gray-600 hover:text-gray-900"
              >
                <Brain className="w-5 h-5" />
                <span>AIQ</span>
              </Link>
              <Link
                to="/my/support"
                className="flex items-center space-x-1 text-gray-600 hover:text-gray-900"
              >
                <Headphones className="w-5 h-5" />
                <span>{t('learner.support')}</span>
              </Link>
              {/* User Menu */}
              <div className="flex items-center space-x-4">
                <Link
                  to="/my/notes"
                  className="relative p-2 text-gray-600 hover:text-gray-900"
                  title={t('learner.notes')}
                >
                  <StickyNote className="w-6 h-6" />
                </Link>

                <NotificationBadge />

                {user?.email?.endsWith('@quebon.com') && (
                  <Link
                    to="/review-contents"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 transition-colors text-sm font-medium"
                  >
                    <ClipboardCheck className="w-4 h-4" />
                    <span>{t('header.contentReview')}</span>
                  </Link>
                )}
                <ModeSwitcher />
                <LanguageSwitcher />

                <Link
                  to="/my/profile"
                  className="flex items-center space-x-2 text-gray-600 transition-colors hover:text-gray-900"
                >
                  <User className="w-5 h-5" />
                  <span className="text-sm">{user?.fullName}</span>
                </Link>

                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-1 text-gray-500 hover:text-gray-700"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <Outlet />
      </main>
    </div>
  )
}

